import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { EditorState, Compartment, Annotation } from '@codemirror/state';
import { EditorView, ViewPlugin, Decoration, keymap, placeholder as cmPlaceholder } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { autocompletion, completionKeymap, snippetCompletion, startCompletion } from '@codemirror/autocomplete';
import { scan } from '../../utils/templatePreview';
import { METHODS } from '../../utils/toolDefinition';
import { templateContext, suggestions } from '../../utils/templateAssist';

/**
 * Editor de texto con plantillas, como el de expresiones de n8n (CodeMirror 6):
 *  - resalta {{dato}}, {{dato | método}} y el código {{= … }} (textos, números, funciones);
 *  - al escribir «{{» cierra con «}}» y sugiere los datos; después de «|» los métodos; dentro de {{= }} las ayudas (fechaMx,
 *    formatoFecha…), los datos y sus campos al escribir «.»; Ctrl+Espacio abre las sugerencias donde sea; Tab salta entre los huecos;
 *  - marca métodos que no existen; con `literalUnknown` deja en gris las variables que el motor no llena (quedan tal cual) y con
 *    `explain(expr)` (devuelve el motivo o null) marca en ROJO, con el motivo al pasar el cursor, lo que el motor no entiende.
 * `vars`: [{ path, label, hint? }]. Una línea salvo `multiline`. Ref: { focus(), insert(texto), view }.
 */
const External = Annotation.define();
const METHOD_NAMES = new Set(METHODS.map((m) => m.name));
const KEYWORDS = /\b(return|const|let|if|else|true|false|null|undefined|new|typeof)\b/g;
const STRINGS = /"(?:\\.|[^"\\])*"?|'(?:\\.|[^'\\])*'?|`(?:\\.|[^`\\])*`?/g;
const NUMBERS = /\b\d+(?:\.\d+)?\b/g;
const CALLS = /[\p{L}_$][\p{L}\p{N}_$]*(?=\s*\()/gu;

function buildMarks(doc, opts) {
  const text = doc.toString(); const out = [];
  const roots = opts.roots;
  for (const t of scan(text)) {
    const inner0 = t.start + 2; const inner1 = t.end - 2;
    const code = t.expr.startsWith('=');
    const root = t.expr.split(/[.|\s[]/)[0];
    // Lo que el motor no entiende va en ROJO, con el motivo al pasar el cursor.
    const why = !code && opts.explain ? opts.explain(t.expr) : null;
    if (why) {
      out.push(Decoration.mark({ class: 'cm-tx', attributes: { title: why } }).range(t.start, t.end));
      continue;
    }
    if (!code && opts.literalUnknown && !opts.literalUnknown(t.expr)) {
      out.push(Decoration.mark({ class: 'cm-tl', attributes: { title: 'El agente lo recibe tal cual: no es un dato que se llene aquí.' } }).range(t.start, t.end));
      continue;
    }
    // La plantilla completa como pastilla: verde si es un dato, ámbar si es código.
    out.push(Decoration.mark({ class: code ? 'cm-tvc' : 'cm-tv', attributes: { title: code ? 'Código: lo calcula el motor' : 'Dato: se reemplaza por su valor' } }).range(t.start, t.end));
    out.push(Decoration.mark({ class: 'cm-tb' }).range(t.start, inner0));
    out.push(Decoration.mark({ class: 'cm-tb' }).range(inner1, t.end));
    if (inner1 <= inner0) continue;
    out.push(Decoration.mark({ class: code ? 'cm-tc' : 'cm-te' }).range(inner0, inner1));
    const inner = text.slice(inner0, inner1);
    if (code) {
      const taken = [];
      for (const m of inner.matchAll(STRINGS)) { taken.push([m.index, m.index + m[0].length]); out.push(Decoration.mark({ class: 'cm-ts' }).range(inner0 + m.index, inner0 + m.index + m[0].length)); }
      const free = (i) => !taken.some(([a, b]) => i >= a && i < b);
      for (const [re, cls] of [[NUMBERS, 'cm-tn'], [KEYWORDS, 'cm-tk'], [CALLS, 'cm-tf']]) {
        for (const m of inner.matchAll(re)) if (free(m.index)) out.push(Decoration.mark({ class: cls }).range(inner0 + m.index, inner0 + m.index + m[0].length));
      }
    } else {
      if (roots && root && !roots.has(root)) {
        const a = inner0 + inner.indexOf(root);
        out.push(Decoration.mark({ class: 'cm-tw', attributes: { title: `No hay un dato «${root}» aquí.` } }).range(a, a + root.length));
      }
      // fecha solo acepta "corta", "larga", "completa" u "hora"
      for (const m of inner.matchAll(/\|\s*fecha\s*:\s*(["']?)([^"'|}\s]*)\1/gu)) {
        if (['corta', 'larga', 'completa', 'hora'].includes(m[2])) continue;
        const a = inner0 + m.index + m[0].length - m[2].length - m[1].length;
        out.push(Decoration.mark({ class: 'cm-tw', attributes: { title: `«${m[2]}» no es un formato de fecha: usa "corta", "larga", "completa" (con hora) u "hora".` } }).range(a, a + Math.max(m[2].length, 1)));
      }
      for (const m of inner.matchAll(/\|\s*([\p{L}_]+)/gu)) {
        const a = inner0 + m.index + m[0].length - m[1].length;
        out.push(Decoration.mark(METHOD_NAMES.has(m[1]) ? { class: 'cm-tm' } : { class: 'cm-tw', attributes: { title: `No existe el método «${m[1]}».` } }).range(a, a + m[1].length));
      }
    }
  }
  return Decoration.set(out, true);
}

// El alto máximo va en el propio editor y el scroll en .cm-scroller: si hace scroll un contenedor de afuera, CodeMirror no se entera y
// deja de dibujar las líneas que entran a la vista.
const theme = (multiline, mono, maxHeight) => EditorView.theme({
  '&': { color: 'rgb(var(--fg))', backgroundColor: 'transparent', fontSize: multiline && !mono ? '14.5px' : '12.5px', minHeight: 'inherit', ...(maxHeight ? { maxHeight } : {}) },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': { minHeight: 'inherit', ...(maxHeight ? { overflow: 'auto' } : {}), fontFamily: mono ? '"JetBrains Mono", ui-monospace, monospace' : 'Figtree, system-ui, sans-serif', lineHeight: multiline ? (mono ? '1.6' : '1.7') : '1.5', overflowX: multiline ? 'visible' : 'auto', marginRight: '40px' },
  '.cm-content': { padding: multiline ? '10px 4px 10px 12px' : '9px 4px 9px 12px', caretColor: 'rgb(var(--live))' },
  '.cm-line': { padding: 0 },
  '.cm-placeholder': { color: 'rgb(var(--fg-faint))' },
  '.cm-tv': { color: 'rgb(var(--live))', backgroundColor: 'rgb(var(--live) / 0.16)', boxShadow: 'inset 0 0 0 1px rgb(var(--live) / 0.55)', borderRadius: '6px', padding: '1px 3px', fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: '0.88em', fontWeight: '600', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' },
  '.cm-tvc': { color: 'rgb(var(--fg))', backgroundColor: 'rgb(var(--caution) / 0.14)', boxShadow: 'inset 0 0 0 1px rgb(var(--caution) / 0.5)', borderRadius: '6px', padding: '1px 3px', fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: '0.88em', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' },
  '.cm-tv .cm-tb': { opacity: '0.7' },
  '.cm-tvc .cm-tb': { color: 'rgb(var(--caution))' },
  '.cm-tb': { fontWeight: '700' },
  '.cm-te': {},
  '.cm-tc': {},
  '.cm-tl': { color: 'rgb(var(--fg-faint))' },
  '.cm-tx': { color: 'rgb(var(--signal))', backgroundColor: 'rgb(var(--signal) / 0.14)', boxShadow: 'inset 0 0 0 1px rgb(var(--signal) / 0.6)', borderRadius: '6px', padding: '1px 3px', textDecoration: 'underline wavy rgb(var(--signal))', textUnderlineOffset: '3px', cursor: 'help' },
  '.cm-ts': { color: 'rgb(var(--caution))' },
  '.cm-tn': { color: 'rgb(var(--live))' },
  '.cm-tk': { color: 'rgb(var(--signal))', fontWeight: '600' },
  '.cm-tf': { color: 'rgb(var(--fg))', fontWeight: '600' },
  '.cm-tm': { fontStyle: 'italic', fontWeight: '700' },
  '.cm-tw': { textDecoration: 'underline wavy rgb(var(--signal))', textUnderlineOffset: '3px' },
  '.cm-tooltip': { backgroundColor: 'rgb(var(--panel))', border: '1px solid rgb(var(--line))', borderRadius: '12px', boxShadow: 'var(--shadow)', color: 'rgb(var(--fg))', overflow: 'hidden' },
  '.cm-tooltip-autocomplete > ul': { fontFamily: 'Figtree, system-ui, sans-serif', fontSize: '13px', maxHeight: '18rem', minWidth: '16rem' },
  '.cm-tooltip-autocomplete > ul > li': { padding: '5px 10px', lineHeight: '1.4' },
  '.cm-tooltip-autocomplete > ul > li[aria-selected]': { backgroundColor: 'rgb(var(--live) / 0.16)', color: 'rgb(var(--fg))' },
  '.cm-completionLabel': { fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: '12.5px' },
  '.cm-completionMatchedText': { textDecoration: 'none', color: 'rgb(var(--live))', fontWeight: '700' },
  '.cm-completionDetail': { marginLeft: '10px', fontStyle: 'normal', color: 'rgb(var(--fg-dim))' },
  '.cm-completionInfo': { padding: '8px 10px', maxWidth: '22rem', whiteSpace: 'pre-wrap', fontSize: '12.5px', lineHeight: '1.5', color: 'rgb(var(--fg-dim))' },
  '.cm-snippetField': { backgroundColor: 'rgb(var(--live) / 0.18)', borderRadius: '2px' },
});

const TemplateEditor = forwardRef(function TemplateEditor({
  value, onChange, vars, multiline = false, mono = true, placeholder = '', ariaLabel, readOnly = false, minHeight, maxHeight,
  spellCheck = false, lang, literalUnknown, explain, warnUnknownRoots = false, onFocus, onBlur, onSave, className = '', id,
}, ref) {
  const host = useRef(null);
  const view = useRef(null);
  const latest = useRef({ onChange, onFocus, onBlur, onSave, vars });
  latest.current = { onChange, onFocus, onBlur, onSave, vars };
  const deco = useRef(new Compartment());
  const ro = useRef(new Compartment());

  useImperativeHandle(ref, () => ({
    get view() { return view.current; },
    focus: () => view.current?.focus(),
    insert: (text) => {
      const v = view.current; if (!v) return;
      const { from, to } = v.state.selection.main;
      v.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from + text.length }, scrollIntoView: true });
      v.focus();
    },
    cursor: () => view.current?.state.selection.main.head ?? 0,
  }), []);

  const decoExt = () => {
    const roots = warnUnknownRoots && vars?.length ? new Set(vars.map((x) => x.path.split('.')[0])) : null;
    const opts = { roots, literalUnknown, explain };
    return ViewPlugin.fromClass(class {
      constructor(v) { this.decorations = buildMarks(v.state.doc, opts); }
      update(u) { if (u.docChanged) this.decorations = buildMarks(u.state.doc, opts); }
    }, { decorations: (p) => p.decorations });
  };

  useEffect(() => {
    const source = (cm) => {
      const c = templateContext(cm.state.doc.toString(), cm.pos);
      if (!c) return null;
      const options = suggestions(c, latest.current.vars || []).map((s) => {
        const base = { label: s.label, detail: s.detail, info: s.info, type: s.type, boost: s.boost };
        if (s.snippet) return snippetCompletion(s.snippet, base);
        if (s.replaceDot) return { ...base, apply: (v, _c, from, to) => v.dispatch({ changes: { from: from - 1, to, insert: s.label }, selection: { anchor: from - 1 + s.label.length } }) };
        return s.apply ? { ...base, apply: s.apply } : base;
      });
      if (!options.length) return null;
      return { from: c.from, options, validFor: c.kind === 'path' ? /^[\p{L}\p{N}_$.]*$/u : /^[\p{L}\p{N}_$]*$/u };
    };
    const openBraces = EditorView.inputHandler.of((v, from, to, text) => {
      // «{» después de «{», o «{{» de una vez (algunos teclados y el dictado lo mandan junto)
      const pair = text === '{{' || (text === '{' && v.state.sliceDoc(from - 1, from) === '{');
      if (!pair || v.state.sliceDoc(to, to + 2) === '}}') return false;
      const insert = text === '{{' ? '{{  }}' : '{  }}';
      v.dispatch({ changes: { from, to, insert }, selection: { anchor: from + insert.length - 3 } });
      startCompletion(v);
      return true;
    });
    // Después de «.», «|» o «{{=» se abren las sugerencias aunque no se haya escrito una letra.
    const reopen = EditorView.updateListener.of((u) => {
      if (!u.docChanged || !u.view.hasFocus) return;
      const head = u.state.selection.main.head; const prev = u.state.sliceDoc(head - 1, head);
      if (['.', '|', '='].includes(prev) || (prev === ' ' && /[|=]\s$/.test(u.state.sliceDoc(head - 2, head)))) {
        if (templateContext(u.state.doc.toString(), head)) setTimeout(() => startCompletion(u.view), 0);
      }
    });
    const singleLine = EditorState.transactionFilter.of((tr) => {
      if (multiline || !tr.docChanged || !tr.newDoc.toString().includes('\n')) return tr;
      return [tr, { changes: { from: 0, to: tr.newDoc.length, insert: tr.newDoc.toString().replace(/\r?\n/g, ' ') }, sequential: true }];
    });
    const state = EditorState.create({
      doc: value || '',
      extensions: [
        history(),
        keymap.of([
          { key: 'Mod-s', run: () => { latest.current.onSave?.(); return Boolean(latest.current.onSave); } },
          ...completionKeymap, ...historyKeymap, ...defaultKeymap,
        ]),
        autocompletion({ override: [source], icons: false, activateOnTyping: true, closeOnBlur: true, maxRenderedOptions: 60 }),
        openBraces, reopen, singleLine,
        deco.current.of(decoExt()),
        ro.current.of([EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)]),
        multiline ? EditorView.lineWrapping : [],
        cmPlaceholder(placeholder),
        theme(multiline, mono, maxHeight),
        EditorView.contentAttributes.of({ 'aria-label': ariaLabel || '', 'aria-multiline': String(multiline), spellcheck: String(spellCheck), ...(lang ? { lang } : {}), ...(id ? { id } : {}) }),
        EditorView.updateListener.of((u) => {
          if (u.docChanged && !u.transactions.some((t) => t.annotation(External))) latest.current.onChange?.(u.state.doc.toString());
          if (u.focusChanged) (u.view.hasFocus ? latest.current.onFocus : latest.current.onBlur)?.(u.view);
        }),
      ],
    });
    view.current = new EditorView({ state, parent: host.current });
    return () => { view.current?.destroy(); view.current = null; };
    // El editor se crea una vez; valor, sólo lectura y datos se sincronizan abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const v = view.current; if (!v) return;
    const cur = v.state.doc.toString();
    if ((value || '') !== cur) v.dispatch({ changes: { from: 0, to: cur.length, insert: value || '' }, annotations: External.of(true) });
  }, [value]);
  useEffect(() => { view.current?.dispatch({ effects: ro.current.reconfigure([EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)]) }); }, [readOnly]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { view.current?.dispatch({ effects: deco.current.reconfigure(decoExt()) }); }, [vars, warnUnknownRoots, literalUnknown, explain]);

  return (
    <div ref={host} style={{ minHeight }}
      className={`w-full rounded-xl bg-raised/60 border border-line text-fg transition-colors duration-150 hover:border-fg/20 focus-within:border-live focus-within:ring-2 focus-within:ring-live/30 ${readOnly ? 'opacity-80' : ''} ${className}`} />
  );
});

export default TemplateEditor;
